import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiCloud as Cloud,
  FiPlus as Plus,
  FiRefreshCw as RefreshCw,
  FiTrash2 as Trash2,
  FiClock as Clock,
  FiHash as Hash,
  FiCheckCircle as CheckCircle,
  FiAlertTriangle as AlertTriangle,
  FiExternalLink as ExternalLink,
  FiDatabase as Database,
  FiShield as Shield
} from 'react-icons/fi';
import { getDatasetVectorExports, deleteVectorExport, syncVectorExport, createVectorExport } from '../../../../../services/vectorExportsApi';
import { apiCall } from '../../../../../services/api';
import { ConfirmDialog } from '../../../../../components/ui';

interface AwsAccount {
  id: string;
  account_id: string;
  role_name?: string;
  account_type?: string;
}

interface VectorExport {
  id: string;
  display_name: string;
  status: string;
  aws_account_id: string;
  region?: string;
  vector_count?: number;
  last_sync_at?: string | null;
  created_at?: string;
}

interface QuickExportData {
  aws_account_id: string;
  region: string;
}

interface DeleteDialogState {
  isOpen: boolean;
  export: VectorExport | null;
}

interface StatusConfig {
  bg: string;
  text: string;
}

type StatusKey = 'pending' | 'in_progress' | 'completed' | 'failed' | 'syncing';

const STATUS_CONFIG: Record<StatusKey, StatusConfig> = {
  pending: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
  in_progress: { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-700 dark:text-blue-300' },
  completed: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-700 dark:text-green-300' },
  failed: { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-700 dark:text-red-300' },
  syncing: { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-700 dark:text-yellow-300' },
};

const DEFAULT_STATUS_CONFIG: StatusConfig = { bg: 'bg-gray-100', text: 'text-gray-700' };

interface Props {
  datasetId: string;
  datasetName: string;
}

const VectorExportsTab: React.FC<Props> = ({ datasetId, datasetName }) => {
  const navigate = useNavigate();
  const [exports, setExports] = useState<VectorExport[]>([]);
  const [awsAccounts, setAwsAccounts] = useState<AwsAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [showQuickExport, setShowQuickExport] = useState(false);
  const [quickExportData, setQuickExportData] = useState<QuickExportData>({
    aws_account_id: '',
    region: 'us-east-1'
  });
  const [creating, setCreating] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({ isOpen: false, export: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const loadData = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [exportsData, accountsData] = await Promise.all([
        getDatasetVectorExports(datasetId),
        apiCall('/aws/accounts')
      ]) as [unknown, AwsAccount[]];
      const exportsArr = (exportsData as { data?: VectorExport[] }).data ?? (exportsData as VectorExport[]) ?? [];
      setExports(exportsArr);
      setAwsAccounts(accountsData.filter(a => a.account_type !== 'payer' && a.account_type !== 'partner'));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load vector exports';
      console.error('Error loading vector exports:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId]);

  const handleSync = async (e: React.MouseEvent<HTMLButtonElement>, exp: VectorExport): Promise<void> => {
    e.stopPropagation();
    if (syncingId) return;

    setSyncingId(exp.id);
    try {
      await syncVectorExport(exp.id);
      void loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to sync';
      alert(message);
    } finally {
      setSyncingId(null);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>, exp: VectorExport): void => {
    e.stopPropagation();
    setDeleteDialog({ isOpen: true, export: exp });
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteDialog.export) return;

    setIsDeleting(true);
    try {
      await deleteVectorExport(deleteDialog.export.id, false);
      setDeleteDialog({ isOpen: false, export: null });
      void loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete';
      alert(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = (): void => {
    setDeleteDialog({ isOpen: false, export: null });
  };

  const handleQuickExport = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    try {
      await createVectorExport({
        dataset_id: datasetId,
        aws_account_id: quickExportData.aws_account_id,
        display_name: `${datasetName} Vectors`,
        region: quickExportData.region,
        auto_sync: false,
        embedding_source: 'aivilo'
      });
      setShowQuickExport(false);
      setQuickExportData({ aws_account_id: '', region: 'us-east-1' });
      void loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create export';
      alert(message);
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status: string): React.ReactNode => {
    const isKnownStatus = (s: string): s is StatusKey =>
      ['pending', 'in_progress', 'completed', 'failed', 'syncing'].includes(s);
    const config = isKnownStatus(status) ? STATUS_CONFIG[status] : DEFAULT_STATUS_CONFIG;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${config.bg} ${config.text}`}>
        {status === 'completed' && <CheckCircle className="w-3 h-3" />}
        {status === 'failed' && <AlertTriangle className="w-3 h-3" />}
        {(['in_progress', 'syncing'].includes(status)) && <RefreshCw className="w-3 h-3 animate-spin" />}
        {status}
      </span>
    );
  };

  const formatTimestamp = (timestamp: string | null | undefined): string => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleDateString();
  };

  const getAccountInfo = (awsAccountId: string): string => {
    const account = awsAccounts.find(a => a.id === awsAccountId);
    return account?.account_id ?? 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      {/* RAG Foundation Info Box */}
      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Database className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">Your RAG Foundation</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Export your datasets as vectors to YOUR AWS account. Build RAG chatbots, semantic search, and AI apps with full data ownership.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Vector Exports
        </h2>
        <button
          onClick={() => setShowQuickExport(true)}
          disabled={awsAccounts.length === 0}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          title={awsAccounts.length === 0 ? 'Configure an AWS account first' : ''}
        >
          <Plus className="w-4 h-4" />
          Export to S3 Vectors
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Quick Export Form */}
      {showQuickExport && (
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Quick Export</h3>
          <form onSubmit={(e) => { void handleQuickExport(e); }} className="flex items-end gap-3">
            <div className="flex-1">
              <label htmlFor="aws-account" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">AWS Account</label>
              <select id="aws-account"
                required
                value={quickExportData.aws_account_id}
                onChange={(e) => setQuickExportData({ ...quickExportData, aws_account_id: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Select account...</option>
                {awsAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>Account {acc.account_id}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="region" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Region</label>
              <select id="region"
                value={quickExportData.region}
                onChange={(e) => setQuickExportData({ ...quickExportData, region: e.target.value })}
                className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="us-east-1">us-east-1</option>
                <option value="us-west-2">us-west-2</option>
                <option value="eu-west-1">eu-west-1</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={creating || !quickExportData.aws_account_id}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setShowQuickExport(false)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {awsAccounts.length === 0 && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm text-yellow-700 dark:text-yellow-300">
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          Configure an AWS account in{' '}
          <button onClick={() => navigate('/hub/aws-environments')} className="underline">
            Hub &gt; AWS Environments
          </button>
          {' '}to export vectors.
        </div>
      )}

      {exports.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Database className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="font-medium text-lg text-gray-900 dark:text-white mb-2">Ready to build your RAG pipeline?</h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-4">
            Export this dataset to S3 Vectors in your AWS account. You retain full ownership and can query from your own applications.
          </p>
          {awsAccounts.length > 0 && (
            <button
              onClick={() => setShowQuickExport(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Vector Export
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {exports.map(exp => (
            <div
              key={exp.id}
              onClick={() => navigate(`/ai-operations/vector-exports/${exp.id}`)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/ai-operations/vector-exports/${exp.id}`); }} role="button" tabIndex={0}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">{exp.display_name}</span>
                  {getStatusBadge(exp.status)}
                  <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                    <Shield className="w-3 h-3" />
                    Your AWS
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <Cloud className="w-3 h-3" />
                    AWS {getAccountInfo(exp.aws_account_id)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Hash className="w-3 h-3" />
                    {exp.vector_count ?? 0} vectors
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTimestamp(exp.last_sync_at ?? exp.created_at)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => { void handleSync(e, exp); }}
                  disabled={syncingId === exp.id || exp.status === 'in_progress'}
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                  title="Sync"
                >
                  <RefreshCw className={`w-4 h-4 ${syncingId === exp.id ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={(e) => handleDeleteClick(e, exp)}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <ExternalLink className="w-4 h-4 text-gray-400 ml-1" />
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={() => { void handleDeleteConfirm(); }}
        title="Delete Vector Export"
        message={
          <p>
            Are you sure you want to delete &ldquo;<strong>{deleteDialog.export?.display_name}</strong>&rdquo;?
          </p>
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default VectorExportsTab;
